<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { PALETTE_ITEMS } from '../../constants/csmlNodeTemplates';
import {
  CATEGORY_STYLES,
  NODE_CATEGORY_IDS,
} from '../../constants/csmlNodeCategories';

const emit = defineEmits(['add']);

const RECENT_LIMIT = 4;

const { t } = useI18n();

const search = ref('');
const searchWrap = ref(null);
const collapsed = useLocalStorage('csml-builder-palette-collapsed', false);
const closedSections = useLocalStorage('csml-builder-palette-closed', []);
const recent = useLocalStorage('csml-builder-recent-nodes', []);

const searching = computed(() => !!search.value.trim());

// Palette entries are grouped by the same categories that tint the node
// cards, so the swatch a user picks from is the swatch they get on canvas.
const groups = computed(() => {
  const term = search.value.trim().toLowerCase();
  return NODE_CATEGORY_IDS.filter(id => id !== 'start')
    .map(id => ({
      id,
      labelKey: `AGENT_BOTS.VISUAL_BUILDER.CATEGORIES.${id.toUpperCase()}`,
      iconClass: CATEGORY_STYLES[id].paletteIcon,
      dotClass: CATEGORY_STYLES[id].portDot,
      items: PALETTE_ITEMS.filter(item => item.category === id).filter(
        item =>
          !term ||
          t(item.labelKey).toLowerCase().includes(term) ||
          t(item.descKey).toLowerCase().includes(term)
      ),
    }))
    .filter(group => group.items.length);
});

const legendIds = NODE_CATEGORY_IDS;

const recentItems = computed(() =>
  recent.value
    .map(type => PALETTE_ITEMS.find(item => item.type === type))
    .filter(Boolean)
);

const iconClassFor = item => CATEGORY_STYLES[item.category].paletteIcon;

const isOpen = group =>
  searching.value || !closedSections.value.includes(group.id);

const toggleSection = group => {
  closedSections.value = closedSections.value.includes(group.id)
    ? closedSections.value.filter(id => id !== group.id)
    : [...closedSections.value, group.id];
};

const remember = type => {
  recent.value = [type, ...recent.value.filter(item => item !== type)].slice(
    0,
    RECENT_LIMIT
  );
};

const onAdd = type => {
  remember(type);
  emit('add', type);
};

const onDragStart = (event, type) => {
  event.dataTransfer.setData('text/plain', type);
  event.dataTransfer.effectAllowed = 'copy';
  remember(type);
};

// "/" jumps to the search box, as long as the user is not already typing.
const onSlash = event => {
  if (event.key !== '/' || collapsed.value) return;
  const { tagName, isContentEditable } = event.target;
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || isContentEditable) {
    return;
  }
  event.preventDefault();
  searchWrap.value?.querySelector('input')?.focus();
};

onMounted(() => window.addEventListener('keydown', onSlash));
onBeforeUnmount(() => window.removeEventListener('keydown', onSlash));
</script>

<template>
  <aside
    class="absolute z-30 flex flex-col overflow-hidden transition-[width] duration-150 border shadow-lg top-3 bottom-3 start-3 rounded-xl border-n-weak bg-n-solid-1/95 backdrop-blur motion-reduce:transition-none"
    :class="collapsed ? 'w-12' : 'w-60'"
  >
    <header
      class="sticky top-0 z-10 flex flex-col gap-2 p-2 border-b shrink-0 border-n-weak bg-n-solid-1"
      :class="{ 'items-center': collapsed }"
    >
      <div class="flex items-center justify-between gap-2">
        <div v-if="!collapsed" class="min-w-0 ps-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('AGENT_BOTS.VISUAL_BUILDER.PALETTE_TITLE') }}
          </h3>
          <p class="mt-0.5 text-xs text-n-slate-11">
            {{ t('AGENT_BOTS.VISUAL_BUILDER.PALETTE_HINT') }}
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          :icon="
            collapsed ? 'i-lucide-panel-left-open' : 'i-lucide-panel-left-close'
          "
          is-icon-only
          class="rtl:-scale-x-100"
          :title="
            collapsed
              ? t('AGENT_BOTS.VISUAL_BUILDER.PALETTE_EXPAND')
              : t('AGENT_BOTS.VISUAL_BUILDER.PALETTE_COLLAPSE')
          "
          @click="collapsed = !collapsed"
        />
      </div>
      <div v-if="!collapsed" ref="searchWrap">
        <Input
          v-model="search"
          size="sm"
          :placeholder="t('AGENT_BOTS.VISUAL_BUILDER.PALETTE_SEARCH')"
        />
      </div>
    </header>

    <div
      v-if="collapsed"
      class="flex flex-col items-center flex-1 gap-1 p-1.5 overflow-y-auto"
    >
      <template v-for="group in groups" :key="group.id">
        <span
          v-for="item in group.items"
          :key="item.type"
          class="flex items-center justify-center rounded-md cursor-grab active:cursor-grabbing size-8 shrink-0 transition-transform duration-150 hover:scale-105"
          :class="group.iconClass"
          draggable="true"
          :title="t(item.labelKey)"
          @click="onAdd(item.type)"
          @dragstart="onDragStart($event, item.type)"
        >
          <Icon :icon="item.icon" class="size-4" />
        </span>
      </template>
    </div>

    <div v-else class="flex flex-col flex-1 gap-2 p-2 overflow-y-auto">
      <p v-if="!groups.length" class="px-1 py-4 text-xs text-n-slate-11">
        {{ t('AGENT_BOTS.VISUAL_BUILDER.PALETTE_NO_RESULTS') }}
      </p>

      <section
        v-if="!searching && recentItems.length"
        class="flex flex-col gap-1.5"
      >
        <h4
          class="px-1 text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('AGENT_BOTS.VISUAL_BUILDER.PALETTE_RECENT') }}
        </h4>
        <div class="grid grid-cols-2 gap-1.5">
          <div
            v-for="item in recentItems"
            :key="`recent-${item.type}`"
            class="flex items-center gap-2 px-2 py-1.5 border rounded-lg cursor-grab active:cursor-grabbing border-n-weak bg-n-alpha-1 hover:border-n-brand hover:bg-n-solid-2 transition-colors duration-150"
            draggable="true"
            :title="t(item.descKey)"
            @click="onAdd(item.type)"
            @dragstart="onDragStart($event, item.type)"
          >
            <span
              class="flex items-center justify-center rounded-md size-6 shrink-0"
              :class="iconClassFor(item)"
            >
              <Icon :icon="item.icon" class="size-3.5" />
            </span>
            <span class="text-[11px] font-medium truncate text-n-slate-12">
              {{ t(item.labelKey) }}
            </span>
          </div>
        </div>
      </section>

      <section
        v-for="group in groups"
        :key="group.id"
        class="flex flex-col gap-1.5"
      >
        <button
          type="button"
          class="flex items-center gap-2 px-1 py-0.5 text-start rounded-md hover:bg-n-alpha-1"
          :aria-expanded="isOpen(group)"
          @click="toggleSection(group)"
        >
          <span class="rounded-full size-2 shrink-0" :class="group.dotClass" />
          <span
            class="flex-1 text-[10px] font-semibold tracking-wide uppercase text-n-slate-11"
          >
            {{ t(group.labelKey) }}
          </span>
          <span class="text-[10px] text-n-slate-10">
            {{ group.items.length }}
          </span>
          <Icon
            icon="i-lucide-chevron-down"
            class="size-3 transition-transform text-n-slate-10"
            :class="{ '-rotate-90 rtl:rotate-90': !isOpen(group) }"
          />
        </button>
        <div v-if="isOpen(group)" class="grid grid-cols-2 gap-1.5">
          <div
            v-for="item in group.items"
            :key="item.type"
            class="flex items-center gap-2 px-2 py-1.5 border rounded-lg cursor-grab active:cursor-grabbing border-n-weak bg-n-alpha-1 hover:border-n-brand hover:bg-n-solid-2 transition-colors duration-150"
            draggable="true"
            :title="t(item.descKey)"
            @click="onAdd(item.type)"
            @dragstart="onDragStart($event, item.type)"
          >
            <span
              class="flex items-center justify-center rounded-md size-6 shrink-0"
              :class="group.iconClass"
            >
              <Icon :icon="item.icon" class="size-3.5" />
            </span>
            <span class="text-[11px] font-medium truncate text-n-slate-12">
              {{ t(item.labelKey) }}
            </span>
          </div>
        </div>
      </section>
    </div>

    <footer
      v-if="!collapsed"
      class="flex flex-wrap items-center px-3 py-2 border-t gap-x-3 gap-y-1 shrink-0 border-n-weak"
    >
      <span
        v-for="id in legendIds"
        :key="id"
        class="flex items-center gap-1 text-[10px] text-n-slate-11"
      >
        <span
          class="rounded-full size-2"
          :class="CATEGORY_STYLES[id].portDot"
        />
        {{ t(`AGENT_BOTS.VISUAL_BUILDER.CATEGORIES.${id.toUpperCase()}`) }}
      </span>
    </footer>
  </aside>
</template>
